import { isChatHour } from '~/utils/chat-hours'

// Pages where the messenger launcher is visible
const CHAT_PATHS = ['/ayuda/contacto']

// Remembers across reloads that Zendesk holds an authenticated session, so a
// logout that happened while the widget wasn't loaded (inactivity timeout,
// redirect, reload) is still applied on the next widget load.
const AUTH_KEY = 'zd_auth_user'

export default ({ app, $config, store }, inject) => {
  let readyPromise = null
  let loggedInAs = null
  let isOpen = false

  // ---- adapt to your app ----
  const getUser = () => store.state.auth.user
  // ---------------------------

  // JWT signed by the backend and returned with the login response
  const getToken = async () => getUser()?.zendesk_jwt

  const getAuthFlag = () => {
    try { return localStorage.getItem(AUTH_KEY) } catch (e) { return null }
  }
  const setAuthFlag = (id) => {
    try {
      if (id) localStorage.setItem(AUTH_KEY, String(id))
      else localStorage.removeItem(AUTH_KEY)
    } catch (e) {}
  }

  const isChatRoute = (route) =>
      CHAT_PATHS.some(p => route.path.replace(/\/$/, '').endsWith(p))

  // Single place that decides whether the launcher is visible
  const applyVisibility = (route = app.router.currentRoute) => {
    if (!window.zE) return
    const show = isChatRoute(route) && isChatHour()
    if (show) {
      window.zE('messenger', 'show')
    } else if (!isOpen) {
      // keep it visible if the user is mid-conversation
      window.zE('messenger', 'hide')
    }
  }

  const syncAuth = async () => {
    if (!readyPromise) return            // runs again once the script loads
    const zE = await readyPromise
    const user = getUser()
    const userId = user?.id ? String(user.id) : null

    if (userId && loggedInAs !== userId) {
      loggedInAs = userId
      setAuthFlag(userId)
      zE('messenger', 'loginUser', (callback) => {
        getToken().then(callback).catch(e => console.error('[zendesk] token', e))
      })
    } else if (!userId && (loggedInAs || getAuthFlag())) {
      // covers logouts that happened while the widget wasn't loaded
      loggedInAs = null
      setAuthFlag(null)
      zE('messenger', 'logoutUser')
    }
  }

  const load = () => {
    if (readyPromise) return readyPromise
    readyPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = `https://static.zdassets.com/ekr/snippet.js?key=${$config.zdWidgetKey}`
      s.id = 'ze-snippet'
      s.async = true
      s.onload = () => {
        window.zE('messenger:on', 'open', () => { isOpen = true })
        window.zE('messenger:on', 'close', () => {
          isOpen = false
          applyVisibility()              // hide again if closed on a non-chat page
        })
        applyVisibility()
        resolve(window.zE)
        syncAuth()
      }
      s.onerror = reject
      document.body.appendChild(s)
    })
    return readyPromise
  }

  // Lazy-load on first interaction
  const events = ['scroll', 'click', 'mousemove', 'keypress', 'touchstart']
  const onFirstInteraction = () => {
    events.forEach(e => window.removeEventListener(e, onFirstInteraction))
    load()
  }
  events.forEach(e => window.addEventListener(e, onFirstInteraction, { once: true, passive: true }))

  // Re-apply on every navigation; load immediately on chat pages
  app.router.afterEach((to) => {
    if (isChatRoute(to)) {
      load().then(() => applyVisibility(to)).catch(() => {})
    } else {
      applyVisibility(to)
    }
  })

  // Initial page (afterEach doesn't fire for the first SSR-rendered route)
  if (isChatRoute(app.router.currentRoute)) {
    load().catch(() => {})
  }

  store.watch(() => getUser()?.id, syncAuth)

  inject('zendesk', {
    load,
    syncAuth,
    // For logout paths that don't clear auth.user in the store
    // (e.g. a session-expired interceptor that redirects or reloads).
    logout() {
      loggedInAs = null
      if (window.zE) {
        setAuthFlag(null)
        window.zE('messenger', 'logoutUser')
      }
      // if the widget isn't loaded, keep the flag: syncAuth sees it on the
      // next load (with no user in the store) and calls logoutUser then
    },
    refresh: () => applyVisibility(),
    isOpen: () => isOpen,
    async toggle() {
      const zE = await load()
      if (isOpen) {
        zE('messenger', 'close')
      } else {
        zE('messenger', 'show')
        zE('messenger', 'open')
      }
    },
  })
}